<script setup>
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import WhitelabelEmailWarning from "@/components/global-themed/WhitelabelEmailWarning.vue";
import {computed, ref, watch} from "vue";
import {kitName} from "@/composables/useLocalDomains";
import useApprovalSettings from "@/composables/approval-center/settings/useApprovalSettings";

const {kitEmailRecipientsQuery, saveKitEmailRecipientsMutation, settingsQuery} = useApprovalSettings();
const {isLoading, data} = kitEmailRecipientsQuery;
const {mutate: saveRecipients, isPending: saving} = saveKitEmailRecipientsMutation;

const selectedRecipients = ref([]);

const availableUsers = computed(() => data.value?.available_users || []);
const currentRecipients = computed(() => data.value?.selected_recipients || []);
const agencyRecipients = computed(() => data.value?.agency_recipients || []);
const missingWhitelabelSetup = computed(() => data.value?.missing_whitelabel_setup || false);

watch([currentRecipients, isLoading], ([recipients, loading]) => {
  if (!loading && recipients) {
    selectedRecipients.value = recipients;
  }
}, {
  immediate: true,
});

const emailFrequencySettingData = computed(() => settingsQuery.data.value?.website_email_setting || []);
const emailFrequencySetting = computed(() => {
  if (emailFrequencySettingData.value?.website_setting) {
    return emailFrequencySettingData.value.website_setting;
  }

  return 'agency_default';
});

const isDisabled = computed(() => {
  return emailFrequencySetting.value === 'off' || emailFrequencySetting.value === 'agency_default';
});

const isNotificationsOff = computed(() => emailFrequencySetting.value === 'off' || emailFrequencySetting.value === 'agency_default');

const disabledSelectClasses = computed(() => {
  if (!isNotificationsOff.value) {
    return {};
  }

  return {
    containerDisabled: '!tw-cursor-not-allowed !tw-bg-neutral-100 !tw-border-neutral-200',
    placeholder: '!tw-text-neutral-400',
    search: '!tw-text-neutral-400',
  };
});

watch(emailFrequencySetting, (newFrequency) => {
  if (newFrequency === 'agency_default') {
    selectedRecipients.value = agencyRecipients.value;

    return;
  }

  selectedRecipients.value = currentRecipients.value;
}, {
  immediate: true
})

const save = () => {
  if (isLoading.value || isDisabled.value) {
    return;
  }

  if (JSON.stringify(selectedRecipients.value) === JSON.stringify(currentRecipients.value)) {
    return;
  }

  saveRecipients({
    recipient_ids: selectedRecipients.value,
  });
};

const tooltipText = (user) => {
  if (!user.has_access && !user.has_seo_agent_permission) {
    return `Only users with access to this website and ${kitName.value} permissions can be selected`
  }

  if (!user.has_access) {
    return 'Only users with access to this website can be selected';
  }

  if (!user.has_seo_agent_permission) {
    return `Only users with ${kitName.value} permissions can be selected`;
  }

  return null;
};

const selectOptions = computed(() => {
  return availableUsers.value.map(user => ({
    value: user.id,
    label: `${user.name} (${user.email})`,
    disabled: !user.has_access || !user.has_seo_agent_permission,
    tooltip: tooltipText(user),
  }));
});

const tooltipContent = computed(() => {
  if (emailFrequencySetting.value === 'agency_default') {
    return "This website is using your agency’s default email notification settings. To manage recipients, update the email settings at agency level.";
  }

  if (emailFrequencySetting.value === 'off') {
    return "Email notifications are turned off for this website, so no recipients can be selected.";
  }

  return null;
});
</script>

<template>
  <div class="tw-bg-white tw-rounded-lg tw-p-8 tw-flex tw-flex-col tw-gap-8">
    <div class="tw-flex tw-flex-col tw-gap-6">
      <div class="tw-flex tw-justify-between tw-items-start tw-flex-col tw-gap-6">
        <div class="tw-flex tw-flex-col tw-gap-2 tw-flex-1">
          <p class="tw-font-bold tw-text-neutral-900 tw-text-2.5xl">
            {{ kitName }} Email Recipients
          </p>

          <p class="tw-text-neutral-900 tw-text-base tw-leading-snug">
            Choose which users should receive {{ kitName }} emails for this website. You can select multiple users, and notifications will follow the email settings configured above. Only users with access to this website can receive {{ kitName }} emails. If a user is unavailable or disabled in the list, they may not have the required permissions yet.
          </p>
        </div>

        <WhitelabelEmailWarning v-if="missingWhitelabelSetup">
          <span class="tw-text-base tw-text-neutral-900">
            {{ kitName }} emails are not yet fully active because white label email has not been completed for your organisation. Until setup is finished, only organisation admins will receive {{ kitName }} emails. Other users will not receive notifications yet. Organisation admins may still receive emails during setup, however some emails may temporarily display default branding until both the sending domain and sender email address have been fully configured.
          </span>
        </WhitelabelEmailWarning>

        <div
          class="tw-w-full tw-relative tw-transition-opacity tw-duration-200"
          :class="{ 'tw-opacity-60': isNotificationsOff }"
        >
          <CustomMultiselect
            v-model="selectedRecipients"
            v-tooltip="{
              placement: 'bottom',
              theme: 'dark-blue-tooltip',
              content: tooltipContent,
              disabled: !isDisabled
            }"
            class="tw-text-left tw-h-12.5 tw-relative tw-w-full"
            :classes="disabledSelectClasses"
            :close-on-select="false"
            mode="tags"
            :options="selectOptions"
            :searchable="true"
            :disabled="saving || isLoading || isDisabled"
            placeholder="Select recipients..."
            @close="save"
            @deselect="save"
          />
        </div>
      </div>
    </div>
  </div>
</template>
